import type { GetApiV1ThreeLetterCodePartnerAdminsApplicationConfiguration200 } from '@fukuri-admins/apis/client/gen/model';
import { Loader } from '@fukuri-admins/ui/common';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { isAxiosError } from 'axios';
import { createContext, ReactNode, useContext, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { match, P } from 'ts-pattern';
import { useAuth } from '~/components/auth/AuthProvider';
import { useGetPartnerAdminsApplicationConfiguration } from './hooks/useGetPartnerAdminsApplicationConfiguration';

type ConfigurationContextType = {
  /** アプリケーション設定データ */
  applicationConfiguration: GetApiV1ThreeLetterCodePartnerAdminsApplicationConfiguration200;
};

const ConfigurationContext = createContext<ConfigurationContextType | undefined>(undefined);

type Props = {
  children: ReactNode;
};

/**
 * アプリケーション設定を SWR 経由で取得し、Context として配下に提供する Provider
 *
 * - `useGetPartnerAdminsApplicationConfiguration` を非 suspense で呼び出し、
 *   isLoading / error / data を ts-pattern の match で網羅的に分岐する
 * - document.title の設定は外部システム（DOM）同期のため useEffect で行う（render 中には書かない）
 * - 401（認証切れ）検知時は `onUnauthorized` 経由で `signOut` を発火し（フェッチ解決後の
 *   非同期タイミングで呼ばれるため安全）、ts-pattern では `error` が 401 の axios エラーか
 *   どうかを直接判定して `/login` への `<Navigate>` を返す
 */
export const ConfigurationProvider = ({ children }: Props) => {
  const threeLetterCode = getThreeLetterCode();
  const { signOut } = useAuth();
  const { data, isLoading, error } = useGetPartnerAdminsApplicationConfiguration({
    threeLetterCode,
    onUnauthorized: signOut,
  });

  useEffect(() => {
    const applicationName = data?.application_configuration.application_name;
    if (applicationName) {
      document.title = `${applicationName} | 運営者`;
    }
  }, [data]);

  return match({ isLoading, error, data })
    .with({ isLoading: true }, () => <Loader />)
    .with({ error: P.when(e => isAxiosError(e) && e.response?.status === 401) }, () => (
      <Navigate to='/login' replace />
    ))
    .with({ error: P.not(P.nullish) }, () => <div>Error loading configuration</div>)
    .with({ data: P.not(P.nullish) }, ({ data }) => (
      <ConfigurationContext.Provider value={{ applicationConfiguration: data }}>
        {children}
      </ConfigurationContext.Provider>
    ))
    .otherwise(() => <Loader />);
};

/**
 * ConfigurationContext を参照する hook
 */
export const useConfiguration = () => {
  const context = useContext(ConfigurationContext);
  if (context === undefined) {
    throw new Error('useConfiguration must be used within a ConfigurationProvider');
  }
  return context;
};
